import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import '../styles/FormPage.css';

const UserHierarchyForm = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [users, setUsers] = useState([]);
  const [hierarchies, setHierarchies] = useState([]);

  const [formData, setFormData] = useState({
    parent_user_id: '',
    child_user_id: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [usersResponse, hierarchiesResponse] = await Promise.all([
        api.getUsers(),
        api.getAllUserHierarchies()
      ]);
      
      setUsers(usersResponse.data || []);
      setHierarchies(hierarchiesResponse.data || []);
    } catch (err) {
      setError('Failed to load data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.parent_user_id === formData.child_user_id) {
      setError('A user cannot be assigned as their own parent');
      return;
    }

    try {
      setLoading(true);
      setError('');

      await api.assignUserChild(
        parseInt(formData.parent_user_id),
        parseInt(formData.child_user_id)
      );
      
      setSuccess('User hierarchy assigned successfully!');

      setTimeout(() => {
        navigate('/user-hierarchy');
      }, 1500);
    } catch (err) {
      setError(err.message || 'Failed to assign hierarchy');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/user-hierarchy');
  };

  const getAvailableParents = () => {
    const childId = parseInt(formData.child_user_id);
    if (!childId) return users;
    
    return users.filter(u => u.id !== childId);
  };

  const getAvailableChildren = () => {
    const parentId = parseInt(formData.parent_user_id);
    if (!parentId) return users;
    
    const existingChildIds = hierarchies
      .filter(h => h.parent_user_id === parentId)
      .map(h => h.child_user_id);
    
    return users.filter(u => u.id !== parentId && !existingChildIds.includes(u.id));
  };

  return (
    <div className="form-page-container">
      <div className="form-page-header">
        <div>
          <h1>Assign User Hierarchy</h1>
          <p className="form-page-subtitle">
            Create a parent-child relationship between users
          </p>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      <div className="form-page-content">
        <form onSubmit={handleSubmit}>
          <div className="form-section">
            <h3 className="form-section-title">Hierarchy Assignment</h3>
            
            <div className="info-message">
              <div>
                <strong>How it works:</strong>
                <p>Select a parent user (manager/supervisor) and a child user (subordinate/team member). The parent user will have access to manage the child user's data and activities.</p>
              </div>
            </div>

            <div className="form-grid">
              <div className="form-group">
                <label>Parent User (Manager) *</label>
                <select
                  name="parent_user_id"
                  value={formData.parent_user_id}
                  onChange={handleChange}
                  required
                  disabled={loading}
                >
                  <option value="">Select Parent User</option>
                  {getAvailableParents().map(user => (
                    <option key={user.id} value={user.id}>
                      {user.email} ({user.role})
                    </option>
                  ))}
                </select>
                <small>The user who will manage the child user</small>
              </div>

              <div className="form-group">
                <label>Child User (Subordinate) *</label>
                <select
                  name="child_user_id"
                  value={formData.child_user_id}
                  onChange={handleChange}
                  required
                  disabled={loading}
                >
                  <option value="">Select Child User</option>
                  {getAvailableChildren().map(user => (
                    <option key={user.id} value={user.id}>
                      {user.email} ({user.role})
                    </option>
                  ))}
                </select>
                <small>The user who will be managed</small>
              </div>
            </div>

            {formData.parent_user_id && formData.child_user_id && (
              <div className="info-box">
                <div>
                  <strong>Preview:</strong>
                  <p>
                    <strong>{users.find(u => u.id === parseInt(formData.parent_user_id))?.email}</strong> 
                    {' '} will be able to manage data for {' '}
                    <strong>{users.find(u => u.id === parseInt(formData.child_user_id))?.email}</strong>
                  </p>
                </div>
              </div>
            )}
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? 'Assigning...' : 'Assign Hierarchy'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserHierarchyForm;

